import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import axios from 'axios';
import { BASE_URL } from '../config/config';

/**
 * AI Learning Path Generator
 * Creates personalized learning roadmaps based on goals and current level
 */
const AILearningPath = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logFeatureUsage } = useSubscription();

  // Form state
  const [goal, setGoal] = useState('');
  const [currentLevel, setCurrentLevel] = useState('beginner');
  const [timeframe, setTimeframe] = useState('3');
  const [interests, setInterests] = useState('');
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [learningPath, setLearningPath] = useState(null);
  const [error, setError] = useState('');

  /**
   * Generate learning path using AI
   */
  const handleGenerate = useCallback(async (e) => {
    e.preventDefault();
    
    if (!goal.trim()) {
      setError('Please enter your learning goal');
      return;
    }

    setIsGenerating(true);
    setError('');
    setLearningPath(null);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/ai-subscription/features/learning-path/generate`,
        {
          user_id: user.id,
          goal: goal.trim(),
          current_level: currentLevel,
          timeframe_months: parseInt(timeframe),
          interests: interests.trim(),
          strengths: strengths.trim(),
          weaknesses: weaknesses.trim()
        }
      );

      if (response.data.success) {
        setLearningPath(response.data.data);
        
        // Log feature usage
        await logFeatureUsage('learning_path', {
          goal: goal.trim(),
          timeframe: timeframe
        });
      } else {
        setError(response.data.message || 'Failed to generate learning path');
      }
    } catch (err) {
      console.error('Learning path generation error:', err);
      setError(
        err.response?.data?.message || 
        'Failed to generate learning path. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  }, [goal, currentLevel, timeframe, interests, strengths, weaknesses, user, logFeatureUsage]);

  /**
   * Reset form and results
   */
  const handleReset = useCallback(() => {
    setGoal('');
    setCurrentLevel('beginner');
    setTimeframe('3');
    setInterests('');
    setStrengths('');
    setWeaknesses('');
    setLearningPath(null);
    setError('');
  }, []);

  /**
   * Format learning path text with proper styling
   */
  const formatLearningPath = (text) => {
    if (!text) return '';
    
    // Split by lines and format
    return text.split('\n').map((line, index) => {
      // Headers (## )
      if (line.startsWith('## ')) {
        return (
          <h3 key={index} className="text-xl font-bold text-gray-800 mt-6 mb-3">
            {line.replace('## ', '')}
          </h3>
        );
      }
      
      // Subheaders (### )
      if (line.startsWith('### ')) {
        return (
          <h4 key={index} className="text-lg font-semibold text-gray-700 mt-4 mb-2">
            {line.replace('### ', '')}
          </h4>
        );
      }
      
      // Bold text (**text**)
      if (line.includes('**')) {
        const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-800">$1</strong>');
        return (
          <p key={index} className="mb-2" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
      
      // Bullet points (- )
      if (line.trim().startsWith('- ')) {
        return (
          <li key={index} className="ml-6 mb-1 text-gray-700">
            {line.trim().substring(2)}
          </li>
        );
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <br key={index} />;
      }
      
      // Regular text
      return (
        <p key={index} className="mb-2 text-gray-700">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <PublicHeader />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <button
            onClick={() => navigate('/ai/features')}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to AI Features
          </button>
          
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            🎯 Personalized Learning Path
          </h1>
          <p className="text-gray-600 text-lg">
            Get a customized roadmap tailored to your goals, level, and learning style
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-600 rounded-full w-10 h-10 flex items-center justify-center mr-3">
                1
              </span>
              Your Learning Profile
            </h2>

            <form onSubmit={handleGenerate} className="space-y-6">
              {/* Goal */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Learning Goal *
                </label>
                <input
                  type="text"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="e.g., Master Data Science, Crack JEE Main, Learn Web Development"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  disabled={isGenerating}
                  required
                />
              </div>

              {/* Current Level */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Level *
                </label>
                <select
                  value={currentLevel}
                  onChange={(e) => setCurrentLevel(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  disabled={isGenerating}
                >
                  <option value="beginner">Beginner (Just Starting)</option>
                  <option value="intermediate">Intermediate (Some Experience)</option>
                  <option value="advanced">Advanced (Strong Foundation)</option>
                </select>
              </div>

              {/* Timeframe */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Target Timeframe: {timeframe} months
                </label>
                <input
                  type="range"
                  min="1"
                  max="12"
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  disabled={isGenerating}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 month</span>
                  <span>12 months</span>
                </div>
              </div>

              {/* Interests (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Interests (Optional)
                </label>
                <input
                  type="text"
                  value={interests}
                  onChange={(e) => setInterests(e.target.value)}
                  placeholder="e.g., Machine Learning, Algorithms, Web Design"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  disabled={isGenerating}
                />
              </div>

              {/* Strengths (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Strengths (Optional)
                </label>
                <textarea
                  value={strengths}
                  onChange={(e) => setStrengths(e.target.value)}
                  placeholder="e.g., Good at mathematics, Problem-solving skills"
                  rows="2"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                  disabled={isGenerating}
                />
              </div>

              {/* Weaknesses (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Areas to Improve (Optional)
                </label>
                <textarea
                  value={weaknesses}
                  onChange={(e) => setWeaknesses(e.target.value)}
                  placeholder="e.g., Weak in statistics, Need more practice in coding"
                  rows="2"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                  disabled={isGenerating}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isGenerating || !goal.trim()}
                  className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                    isGenerating || !goal.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating Your Path...
                    </span>
                  ) : (
                    '🚀 Generate My Learning Path'
                  )}
                </button>

                {learningPath && (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Learning Path Display */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="bg-purple-100 text-purple-600 rounded-full w-10 h-10 flex items-center justify-center mr-3">
                2
              </span>
              Your Personalized Path
            </h2>

            {!learningPath && !isGenerating && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🗺️</div>
                <p className="text-gray-500 text-lg">
                  Fill in your details and click "Generate" to get your personalized learning roadmap
                </p>
              </div>
            )}

            {isGenerating && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
                <p className="text-gray-600 text-lg">Crafting your perfect learning journey...</p>
                <p className="text-gray-400 text-sm mt-2">This may take a few moments</p>
              </div>
            )}

            {learningPath && (
              <div className="max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {/* Path Overview */}
                {learningPath.overview && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-2">📋 Overview</h3>
                    <p className="text-blue-800 text-sm">{learningPath.overview}</p>
                  </div>
                )}

                {/* Learning Path Content */}
                <div className="prose max-w-none">
                  {formatLearningPath(learningPath.path || learningPath.plan_text)}
                </div>

                {/* Metadata */}
                {learningPath.generated_at && (
                  <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                    <p>Generated on {new Date(learningPath.generated_at).toLocaleString()}</p>
                    <p className="mt-1">Powered by Gemini AI</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 md:p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">💡 Tips for Best Results</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">🎯</div>
              <h4 className="font-semibold text-gray-800 mb-1">Be Specific</h4>
              <p className="text-sm text-gray-600">
                Clearly define your learning goal for a more targeted path
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">📊</div>
              <h4 className="font-semibold text-gray-800 mb-1">Honest Assessment</h4>
              <p className="text-sm text-gray-600">
                Accurately assess your current level to get appropriate recommendations
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">🔄</div>
              <h4 className="font-semibold text-gray-800 mb-1">Iterate & Refine</h4>
              <p className="text-sm text-gray-600">
                Generate multiple paths with different goals to explore options
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: #3b82f6;
          cursor: pointer;
          border-radius: 50%;
        }
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #3b82f6;
          cursor: pointer;
          border-radius: 50%;
        }
      `}</style>
    </div>
  );
};

export default AILearningPath;
